/**
 * Design Artifacts Hook
 * Manages Design workspace artifacts (Product Architecture, Application Areas) with version history
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type DesignType = 'architecture' | 'areas';

export interface DesignArtifactVersion {
  id: string;
  version: number;
  content: Record<string, unknown>;
  createdAt: string;
  creator: {
    id: string;
    name: string | null;
    email: string;
  };
}

export interface DesignVersion {
  id: string;
  name: string;
  timestamp?: string;
  isCurrent?: boolean;
}

// API Functions
async function fetchDesignVersions(
  projectId: string,
  type: DesignType,
  token: string
): Promise<DesignArtifactVersion[]> {
  const response = await fetch(`/api/projects/${projectId}/design/${type}/versions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // No artifact yet, return empty array
      return [];
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch design versions');
  }

  const data = await response.json();
  return data.data || [];
}

// Transform API response to Version format for the dropdown
function transformToDropdownVersions(versions: DesignArtifactVersion[]): DesignVersion[] {
  if (!versions || versions.length === 0) {
    return [{ id: 'initial', name: 'Version 1', isCurrent: true }];
  }

  return versions.map((v, index) => ({
    id: v.id,
    name: `Version ${v.version}`,
    timestamp: new Date(v.createdAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
    isCurrent: index === 0, // Most recent is current
  }));
}

// Helper to extract markdown from various content shapes
function extractMarkdown(content: unknown): string | null {
  if (!content) return null;
  if (typeof content === 'string' && content.trim()) return content;
  if (typeof content === 'object' && content !== null) {
    const c = content as Record<string, unknown>;
    const md = c.markdown ?? c.content;
    if (typeof md === 'string' && md.trim()) return md;
  }
  return null;
}

// Fetch single design artifact (for content persistence)
async function fetchDesignArtifact(
  projectId: string,
  type: DesignType,
  token: string
): Promise<{ content?: unknown } | null> {
  const response = await fetch(`/api/projects/${projectId}/design/${type}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch design artifact');
  }

  const data = await response.json();
  return data.data ?? null;
}

// Fetch all design artifacts (for sidebar indicators)
async function fetchDesignArtifactsList(
  projectId: string,
  token: string
): Promise<Array<{ type: string; content?: unknown }>> {
  const response = await fetch(`/api/projects/${projectId}/design`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    if (response.status === 404) return [];
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to fetch design artifacts');
  }
  const data = await response.json();
  return data.data ?? [];
}

export function useDesignArtifact(projectId: string | undefined, type: DesignType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['design', projectId, type, 'artifact'],
    queryFn: async () => {
      if (!projectId || !token) return null;
      return fetchDesignArtifact(projectId, type, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useDesignArtifactsList(projectId: string | undefined) {
  const token = useAuthToken();
  return useQuery({
    queryKey: ['design', projectId, 'list'],
    queryFn: async () => {
      if (!projectId || !token) return [];
      return fetchDesignArtifactsList(projectId, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export { extractMarkdown as extractDesignMarkdown };

// Hooks
export function useDesignVersions(projectId: string | undefined, type: DesignType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['design', projectId, type, 'versions'],
    queryFn: async () => {
      if (!projectId || !token) {
        return [];
      }
      const versions = await fetchDesignVersions(projectId, type, token);
      return transformToDropdownVersions(versions);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000, // 30 seconds
  });
}

export function useDesignVersionsRaw(projectId: string | undefined, type: DesignType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['design', projectId, type, 'versions', 'raw'],
    queryFn: async () => {
      if (!projectId || !token) {
        return [];
      }
      return fetchDesignVersions(projectId, type, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}
